import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { finishedSessions, exerciseMap } from '../../db/repo';
import { weeklyVolume, isoWeekRange, VOLUME_RANGE } from '../../domain/volume';
import { MUSCLE_LABEL } from '../../domain/types';
import { todayISO } from '../../lib/dates';
import { IconBack } from '../../ui/icons';

export function VolumeScreen() {
  const navigate = useNavigate();
  const [weekOffset, setWeekOffset] = useState(0);

  const refDate = (() => {
    const d = new Date(todayISO() + 'T00:00:00');
    d.setDate(d.getDate() + weekOffset * 7);
    return todayISO(d);
  })();
  const week = isoWeekRange(refDate);

  const report = useLiveQuery(async () => {
    const [sessions, exById] = await Promise.all([finishedSessions(), exerciseMap()]);
    return weeklyVolume(sessions, exById, week.start, week.end);
  }, [week.start, week.end]);

  return (
    <div className="screen">
      <button className="btn ghost sm" onClick={() => navigate('/more')} style={{ marginBottom: 10 }}>
        <IconBack width={18} height={18} /> More
      </button>
      <div className="screen-title" style={{ marginBottom: 4 }}>Weekly volume</div>

      <div className="spread" style={{ marginBottom: 12 }}>
        <button className="btn sm ghost" onClick={() => setWeekOffset((w) => w - 1)}>Prev</button>
        <span className="faint" style={{ fontSize: 13 }}>{week.start} to {week.end}</span>
        <button className="btn sm ghost" onClick={() => setWeekOffset((w) => w + 1)} disabled={weekOffset >= 0}>Next</button>
      </div>

      <div className="card">
        <div className="spread">
          <div><div className="kpi">{report?.totalWorkingSets ?? 0}</div><div className="kpi-sub">total working sets</div></div>
          <div className="faint" style={{ fontSize: 12, textAlign: 'right' }}>Target per muscle<br />{VOLUME_RANGE.low}-{VOLUME_RANGE.high} sets/wk</div>
        </div>
      </div>

      <div className="section-label">By muscle</div>
      {report?.byMuscle.filter((m) => m.workingSets > 0 || m.frequencyTarget).map((m) => (
        <div key={m.muscle} className="card tight">
          <div className="spread">
            <div className="grow">
              <strong>{MUSCLE_LABEL[m.muscle]}</strong>
              <div className="faint" style={{ fontSize: 12 }}>
                {m.sessions}x this week{m.frequencyTarget ? ` / target ${m.frequencyTarget}x` : ''}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 700, fontSize: 18 }}>{m.workingSets}</div>
              <div className="faint" style={{ fontSize: 11 }}>sets</div>
            </div>
          </div>
          <div className="row" style={{ gap: 6, marginTop: 8 }}>
            {m.frequencyTarget != null && (
              <span className={`chip ${m.meetsFrequency ? 'good' : 'warn'}`}>{m.meetsFrequency ? 'frequency met' : 'under frequency'}</span>
            )}
            <span className={`chip ${m.inVolumeRange ? 'good' : ''}`}>{m.inVolumeRange ? 'in volume range' : m.workingSets < VOLUME_RANGE.low ? 'low volume' : 'high volume'}</span>
          </div>
        </div>
      ))}
      {report && report.totalWorkingSets === 0 && <div className="empty">No working sets logged this week.</div>}
    </div>
  );
}
